package dev.youndie.site.model

import dev.youndie.site.blog.BLOG_ENTRIES

const val SITE_NAME = "kotlin.website"
const val SITE_URL = "https://kotlin.website"
const val GITHUB = "https://github.com/youndie"
const val EMAIL = "panic.xyb@gmail.com"
const val MAVEN_REPO = "https://reposilite.kotlin.website"

/** Where the three cross-posted articles were first published. */
const val MEDIUM = "https://medium.com/@youndie"

/** The handle in the shell prompt; matches the GitHub account, not the given name. */
const val SHELL_USER = "youndie@kotlin.website"

/**
 * The blog is hidden from the nav and the home page while there is nothing in it,
 * so an empty section never ships. Drop a `.md` into `resources/markdown/blog/`
 * and the links come back on the next build — nothing else to switch on.
 */
val BLOG_VISIBLE: Boolean get() = BLOG_ENTRIES.isNotEmpty()

class Library(
    /** Must match the repository name on GitHub — the card links straight to it. */
    val name: String,
    val tagline: String,
    /** First tag renders as the accent chip — keep the platform there. */
    val tags: List<String>,
) {
    val repo: String get() = "$GITHUB/$name"

    /**
     * The library's own page on this site. Cards link here rather than straight to
     * GitHub: sending every visitor and every crawler off-domain leaves one crowded
     * home page to carry twelve different subjects.
     */
    val page: String get() = "/$name"
}

/**
 * Every public repository on github.com/youndie, ordered by what it is worth
 * looking at first rather than by when it was written.
 *
 * Taglines are drawn from each project's README; when a README changes its claims,
 * this list is the thing that quietly goes stale, so it is worth re-reading them
 * together.
 */
val LIBRARIES = listOf(
    Library(
        name = "kompot",
        tagline = "Backend-driven UI for Kotlin Multiplatform: the server describes a screen as a tree, the " +
            "client renders it, and a new screen ships without a client release. Open hierarchies that " +
            "degrade instead of crashing, held by a spec and two conformance kits.",
        // The platform chip stays one word and the four targets become tags of their own: the chip
        // renders accented and a long one dominates the card. mani sets the precedent — plain "kmp".
        tags = listOf("kmp", "jvm", "android", "ios", "wasm", "server-driven ui"),
    ),
    Library(
        name = "shildik",
        tagline = "An OpenID Connect provider in one native binary against one Postgres, where a sign-in " +
            "method is a module rather than a jar loaded into somebody else's process. It replaced Keycloak " +
            "in production and holds 45–48 MiB doing it.",
        tags = listOf("kotlin/native + jvm", "ktor", "oidc", "postgres"),
    ),
    Library(
        name = "tracy",
        tagline = "Logs for Ktor services, shaped for a coding agent to read. One native binary and one " +
            "SQLite file — no JVM, no Elasticsearch, no shipper. MCP is the primary read path, so the " +
            "agent asks for the trace instead of being handed a dashboard link.",
        tags = listOf("kotlin/native + jvm", "ktor", "sqlite", "mcp"),
    ),
    Library(
        name = "zavarnik",
        tagline = "A Gradle plugin for Project Leyden's AOT cache in plain application-plugin apps: trains " +
            "through the real start script, fails the build when production would reject the cache — " +
            "including on the JDK builds that never check the jars — and ships it in the tar and the image.",
        tags = listOf("jvm", "gradle plugin", "leyden", "ktor"),
    ),
    Library(
        name = "booblik",
        tagline = "A message broker on an append-only log: topics into partitions, partitions into segments, " +
            "and an offset the consumer keeps itself. No quorum and no coordinator — everything that makes " +
            "a log fast is reproduced and measured on two rented machines.",
        tags = listOf("jvm", "log storage", "zero-copy", "ffm"),
    ),
    Library(
        name = "petich",
        tagline = "Distributed saga engine built around one question: what is left in the system if you die " +
            "halfway. Steps as interceptors, compensation in reverse order, sagas that pause for a human and " +
            "resume on a later request, and an outbox written in the same transaction as the state change.",
        tags = listOf("jvm", "ktor", "exposed", "postgres"),
    ),
    Library(
        name = "chronik",
        tagline = "A durable timer and nothing else: schedule(id, at, payload), and at that instant the fact " +
            "it is time leaves — never earlier, never lost. The row is written in the same transaction as your " +
            "state change, which is the only reason to have one rather than a cron job.",
        tags = listOf("jvm", "exposed", "postgres"),
    ),
    Library(
        name = "metrik",
        tagline = "Monitoring for Ktor services in one native binary. The agent costs 106 nanoseconds per " +
            "request and a UDP packet a minute; percentiles are merged across instances rather than averaged, " +
            "and every chart carries the deploy markers that explain it.",
        tags = listOf("kotlin/native", "ktor", "compose", "mcp"),
    ),
    Library(
        name = "katcher",
        tagline = "Self-hosted crash tracker for Kotlin: native binary, HTMX UI, embedded SQLite, and login " +
            "delegated to whatever proxy already sits in front. Small enough that nobody has to justify " +
            "running it.",
        tags = listOf("kotlin/native", "htmx", "sqlite"),
    ),
    Library(
        name = "mongkn",
        tagline = "MongoDB for Kotlin/Native, where no official driver exists. A cinterop binding over " +
            "libmongoc behind the shape of the coroutine driver's API — transactions, change streams, and " +
            "read overhead indistinguishable from bare C.",
        tags = listOf("kotlin/native", "cinterop", "mongodb"),
    ),
    Library(
        name = "smtpkn",
        tagline = "SMTP written from RFC 5321 rather than around it, so a service can send mail with no JVM " +
            "in sight. TLS with the certificate chain actually verified, seven SASL mechanisms, and a partial " +
            "refusal returned as a result instead of an exception.",
        tags = listOf("kotlin/native", "openssl", "rfc 5321"),
    ),
    Library(
        name = "bochka",
        tagline = "An S3-compatible object store for one machine, and the same server starts inside a test " +
            "where the JVM otherwise offers a mock. All four body framings, versioning, object lock, " +
            "lifecycle rules that run; 518 of 744 ceph/s3-tests, every failure classified.",
        tags = listOf("jvm", "s3", "zero-copy", "testing"),
    ),
    Library(
        name = "s3kn",
        tagline = "S3 where the AWS SDK does not go — Linux, macOS and iOS as well as the JVM. SigV4 checked " +
            "against all 34 official vectors plus twenty more generated from the reference implementation, " +
            "presigned URLs that work even on targets with no HTTP engine, and every operation run against a " +
            "real server on two platforms.",
        tags = listOf("kmp: linux + apple + jvm", "ktor", "sigv4", "s3"),
    ),
    Library(
        name = "kvadrant-ui",
        tagline = "Metro components for Compose Multiplatform, transcribed from Microsoft's own theme " +
            "resources rather than from screenshots. The eight metrics with no public source ship as " +
            "parameters saying so, and anything nicer than the phone is behind a flag that is off.",
        tags = listOf("compose multiplatform", "jvm", "android", "ios", "wasm", "design system"),
    ),
    Library(
        name = "viddik",
        tagline = "Screenshot testing for Compose Multiplatform that renders through a real Compose Desktop " +
            "window, not LayoutLib. Fixtures are declared with @Preview and buy a golden-file test and " +
            "a live component browser; goldens hold across macOS, Linux and Windows.",
        tags = listOf("compose multiplatform", "ksp", "testing"),
    ),
    Library(
        name = "sborka",
        tagline = "Gradle conventions as a plugin, and a ktlint rule set beside them where every rule names " +
            "the defect it was written for. No configuration and no taste rules: a rule is in the set or it " +
            "is not, and switching one off takes a reason in the annotation.",
        tags = listOf("gradle", "ktlint", "conventions", "static analysis"),
    ),
    Library(
        name = "telek",
        tagline = "Telegram bots as a finite state machine rather than a pile of callbacks. Pure transitions, " +
            "effects on the side, two transports to pick from, published for the JVM and for native Linux so " +
            "a bot can ship as one binary.",
        tags = listOf("kmp: jvm + linux", "telegram", "fsm"),
    ),
    Library(
        name = "appframe",
        tagline = "A window frame for Compose Desktop that behaves like the platform it runs on: traffic " +
            "lights on macOS, Windows 11 button metrics, and on Linux the GTK button-layout setting asked " +
            "directly rather than guessed.",
        tags = listOf("compose desktop", "ui"),
    ),
    Library(
        name = "shashki",
        tagline = "A ride-hailing service written to put the whole stack under load-bearing conditions: two " +
            "Compose Wasm bundles, a Ktor server with two sagas, a vector map drawn in Compose because nothing " +
            "published draws one there, and eleven of these libraries doing real work.",
        tags = listOf("kmp", "compose wasm", "ktor", "sagas", "reference"),
    ),
    Library(
        name = "kore",
        tagline = "The process lifecycle of a Kotlin server binary, owned once: shutdown in a defined " +
            "order, three health questions instead of one, a typed configuration that refuses an unknown " +
            "variable, and /version with the commit. The order is the product — Ktor runs its stop steps " +
            "the opposite way round on the JVM and on Native, from identical source.",
        tags = listOf("kmp: jvm + native", "ktor", "ordered shutdown", "health probes"),
    ),
    Library(
        name = "kafkakn",
        tagline = "A Kafka client for Kotlin Multiplatform: librdkafka linked statically inside the klib on " +
            "Kotlin/Native, the official Java client on the JVM as the oracle. One test suite runs on both " +
            "arms against one broker, and the native arm is right when it agrees.",
        tags = listOf("kmp: jvm + native", "kafka", "librdkafka", "reposilite"),
    ),
    Library(
        name = "xyk",
        tagline = "A durable webhook gateway in a very small container: the event row and the timer that will " +
            "deliver it are committed in one transaction, so an accepted webhook cannot go missing. " +
            "Kotlin/Native, Ktor and SQLite, measured against criteria declared before the code.",
        tags = listOf("kotlin/native", "ktor", "sqlite", "webhooks"),
    ),
    Library(
        name = "mostik",
        tagline = "An HTTP to Kafka bridge whose status code is a true statement about the record: 200 means " +
            "the broker acknowledged it, 429 means it was never queued, 504 means nobody knows. " +
            "Kotlin/Native and JVM from one source, the JVM build the oracle for the native one.",
        tags = listOf("kmp: jvm + native", "ktor", "kafka", "kore"),
    ),
    Library(
        name = "mani",
        tagline = "A budget planner written end to end in Kotlin: Compose clients for Android, iOS, desktop " +
            "and the browser, and a Ktor server that compiles both to the JVM and to a native Linux binary. " +
            "A demo on purpose — it exists to show the whole stack working at once.",
        tags = listOf("kmp", "compose", "ktor", "demo"),
    ),
)
